import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MessageCircle, Loader2, CheckCircle2, ShoppingCart, AlertCircle, Calendar, CreditCard } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

// Pricing constants
const UNIT_PRICE = 3500; // 3,500 F CFA for both fresh and frozen
const MIN_QUANTITY = 2;

// Delivery zones with fees
const DELIVERY_ZONES = [
  { id: 'thies-city', label: 'Thiès Ville', fee: 1500 },
  { id: 'thies-region', label: 'Région de Thiès (hors ville)', fee: 3500 },
  { id: '55-100km', label: '55 à 100 km', fee: 5000 },
  { id: 'above-100km', label: 'Plus de 100 km', fee: null }, // Manual confirmation
];

// Confirmation methods
const CONFIRMATION_METHODS = [
  { id: 'phone', label: 'Appel téléphonique' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'sms', label: 'SMS' },
];

const ProductsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    clientType: '',
    city: '',
    product: '',
    quantity: '2',
    deliveryMode: '',
    deliveryZone: '',
    deliveryDate: '',
    confirmationMethod: '',
    comment: '',
    acceptMarketing: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [quantityError, setQuantityError] = useState('');
  const [createdOrder, setCreatedOrder] = useState(null);
  const [isPaying, setIsPaying] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate quantity
    if (name === 'quantity') {
      const qty = parseInt(value, 10);
      if (isNaN(qty) || qty < MIN_QUANTITY) {
        setQuantityError(`Commande minimum : ${MIN_QUANTITY} poulets.`);
      } else {
        setQuantityError('');
      }
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, acceptMarketing: checked }));
  };

  // Calculate prices
  const priceCalculation = useMemo(() => {
    const quantity = parseInt(formData.quantity, 10) || 0;
    const subtotal = quantity * UNIT_PRICE;
    
    let deliveryFee = 0;
    let deliveryFeeText = '';
    
    if (formData.deliveryMode === 'livraison' && formData.deliveryZone) {
      const zone = DELIVERY_ZONES.find(z => z.id === formData.deliveryZone);
      if (zone) {
        if (zone.fee === null) {
          deliveryFeeText = 'À confirmer';
        } else {
          deliveryFee = zone.fee;
        }
      }
    }
    
    return {
      subtotal,
      deliveryFee,
      deliveryFeeText,
      total: subtotal + deliveryFee,
      isAbove100km: formData.deliveryZone === 'above-100km'
    };
  }, [formData.quantity, formData.deliveryMode, formData.deliveryZone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate minimum quantity
    const qty = parseInt(formData.quantity, 10);
    if (isNaN(qty) || qty < MIN_QUANTITY) {
      toast.error(`Commande minimum : ${MIN_QUANTITY} poulets.`);
      return;
    }
    
    // Validation
    if (!formData.name || !formData.phone || !formData.clientType || !formData.city || 
        !formData.product || !formData.deliveryMode || !formData.confirmationMethod || !formData.deliveryDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Require delivery zone if delivery is selected
    if (formData.deliveryMode === 'livraison' && !formData.deliveryZone) {
      toast.error('Veuillez sélectionner une zone de livraison');
      return;
    }

    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Format phone number
      let formattedPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
      if (!formattedPhone.startsWith('+221') && !formattedPhone.startsWith('221')) {
        formattedPhone = '+221' + formattedPhone;
      } else if (formattedPhone.startsWith('221') && !formattedPhone.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }
      
      const orderData = {
        name: formData.name,
        phone: formattedPhone,
        clientType: formData.clientType,
        city: formData.city,
        product: formData.product,
        quantity: parseInt(formData.quantity, 10),
        deliveryMode: formData.deliveryMode,
        deliveryZone: formData.deliveryMode === 'livraison' ? formData.deliveryZone : null,
        deliveryDate: formData.deliveryDate,
        confirmationMethod: formData.confirmationMethod,
        comment: formData.comment || '',
        acceptMarketing: formData.acceptMarketing
      };

      const response = await fetch(`${BACKEND_URL}/api/orders/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });
      
      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        toast.error('Erreur serveur', { description: 'Réponse invalide du serveur' });
        return;
      }
      
      if (!response.ok) {
        let errorMessage = 'Erreur inconnue';
        if (responseData.detail) {
          if (Array.isArray(responseData.detail)) {
            errorMessage = responseData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else {
            errorMessage = responseData.detail;
          }
        }
        toast.error('Erreur de commande', { description: errorMessage });
        return;
      }
      
      setIsSuccess(true);
      setCreatedOrder(responseData);
      toast.success('Commande enregistrée avec succès !', {
        description: `Numéro de commande : ${responseData.id}`
      });
      
      // Note: form is intentionally NOT auto-reset so the user can choose
      // to pay online via PayTech (button rendered in the success card).
      // To start a new order they click "Nouvelle commande".
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Erreur de connexion', {
        description: 'Impossible de contacter le serveur. Veuillez réessayer.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get today's date for min date in date picker
  const today = new Date().toISOString().split('T')[0];

  const handlePayOnline = async () => {
    if (!createdOrder) return;
    setIsPaying(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: createdOrder.id,
          amount: createdOrder.totalAmount,
          itemName: `${createdOrder.quantity} x ${createdOrder.product === 'poulet-frais' ? 'Poulet frais' : 'Poulet congele'}`,
          commandName: `Commande Volaille Prime ${createdOrder.id.slice(0, 8)}`,
          customerPhone: createdOrder.phone,
          customerName: createdOrder.name,
        }),
      });
      const text = await r.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Reponse invalide du serveur' }; }
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Erreur de paiement');
      if (!data.payment_url) throw new Error('URL de paiement manquante');
      // Redirect to PayTech (or mock) checkout
      window.location.href = data.payment_url;
    } catch (e) {
      toast.error('Echec de l\'initialisation du paiement', { description: e.message });
      setIsPaying(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', phone: '', clientType: '', city: '', product: '',
      quantity: '2', deliveryMode: '', deliveryZone: '', deliveryDate: '',
      confirmationMethod: '', comment: '', acceptMarketing: false,
    });
    setIsSuccess(false);
    setCreatedOrder(null);
  };

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      
      {/* Page Header */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-burgundy to-burgundy-dark text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Commander chez Volaille Prime
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Remplissez le formulaire ci-dessous pour passer votre commande. Nos équipes vous recontacteront rapidement pour confirmation.
          </p>
        </div>
      </section>

      {/* Order Form Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-burgundy/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          {/* WhatsApp Alternative */}
          <div className="text-center mb-8">
            <a 
              href="https://wa.me/221781270846?text=Bonjour,%20je%20souhaite%20commander%20des%20poulets"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <MessageCircle size={20} />
                Commander rapidement via WhatsApp
              </Button>
            </a>
            
            <div className="flex flex-wrap gap-3 justify-center mt-4 text-sm text-gray-600">
              <a href="tel:+221781270846" className="hover:text-burgundy transition-colors">
                📞 +221 78 127 08 46
              </a>
              <span>|</span>
              <a href="tel:+221779093914" className="hover:text-burgundy transition-colors">
                📞 +221 77 909 39 14
              </a>
            </div>

            <div className="flex items-center gap-4 justify-center max-w-md mx-auto mt-6">
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-gray-500 text-sm">OU</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>
          </div>

          {/* Order Form */}
          <Card className="max-w-3xl mx-auto p-8 md:p-12 bg-white shadow-2xl border-2 border-gray-100">
            {isSuccess && createdOrder ? (
              <div className="space-y-6" data-testid="order-success-panel">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <CheckCircle2 className="text-green-600" size={32} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-burgundy">Commande enregistrée !</h2>
                    <p className="text-sm text-gray-500">N° {createdOrder.id}</p>
                  </div>
                </div>

                <div className="bg-cream/60 border border-gold/30 rounded-xl p-6 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Produit</span>
                    <span className="font-semibold">
                      {createdOrder.quantity} × {createdOrder.product === 'poulet-frais' ? 'Poulet frais' : 'Poulet congelé'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Sous-total</span>
                    <span>{Number(createdOrder.subtotal).toLocaleString('fr-FR')} F CFA</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Livraison</span>
                    <span>{Number(createdOrder.deliveryFee || 0).toLocaleString('fr-FR')} F CFA</span>
                  </div>
                  <div className="border-t border-gold/30 pt-3 flex justify-between text-lg">
                    <span className="font-bold text-burgundy">Total</span>
                    <span className="font-bold text-burgundy">{Number(createdOrder.totalAmount).toLocaleString('fr-FR')} F CFA</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handlePayOnline}
                    disabled={isPaying}
                    size="lg"
                    className="w-full bg-gold hover:bg-gold-dark text-burgundy font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                    data-testid="pay-online-btn"
                  >
                    {isPaying ? (
                      <><Loader2 className="animate-spin mr-2" size={20} /> Redirection...</>
                    ) : (
                      <><CreditCard className="mr-2" size={20} /> Payer en ligne maintenant ({Number(createdOrder.totalAmount).toLocaleString('fr-FR')} F CFA)</>
                    )}
                  </Button>
                  <p className="text-xs text-center text-gray-500">
                    Wave • Orange Money • Free Money • Carte Bancaire (via PayTech)
                  </p>

                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-gray-300"></div>
                    <span className="text-gray-500 text-sm">OU</span>
                    <div className="flex-1 h-px bg-gray-300"></div>
                  </div>

                  <Button
                    onClick={resetForm}
                    variant="outline"
                    size="lg"
                    className="w-full"
                    data-testid="new-order-btn"
                  >
                    Confirmer manuellement à la livraison
                  </Button>
                  <p className="text-xs text-center text-gray-500">
                    Notre équipe vous contactera pour finaliser le paiement.
                  </p>
                </div>
              </div>
            ) : (
            <>
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-burgundy/10 rounded-xl">
                <ShoppingCart className="text-burgundy" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-burgundy">Formulaire de commande</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-900 font-medium">
                  Nom & Prénom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Votre nom complet"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="border-gray-300 focus:border-burgundy focus:ring-burgundy"
                  data-testid="name-input"
                  required
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-900 font-medium">
                  Téléphone (WhatsApp) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="77 123 45 67"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="border-gray-300 focus:border-burgundy focus:ring-burgundy"
                  data-testid="phone-input"
                  required
                />
              </div>

              {/* Client Type */}
              <div className="space-y-2">
                <Label className="text-gray-900 font-medium">
                  Type de client <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.clientType}
                  onValueChange={(value) => handleSelectChange('clientType', value)}
                >
                  <SelectTrigger className="border-gray-300 focus:border-burgundy focus:ring-burgundy" data-testid="client-type-select">
                    <SelectValue placeholder="Sélectionnez votre profil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="particulier">Particulier</SelectItem>
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                    <SelectItem value="revendeur">Revendeur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* City / Quarter */}
              <div className="space-y-2">
                <Label htmlFor="city" className="text-gray-900 font-medium">
                  Ville / Quartier <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  placeholder="Ex: Thiès, Dakar, Mbour..."
                  value={formData.city}
                  onChange={handleInputChange}
                  className="border-gray-300 focus:border-burgundy focus:ring-burgundy"
                  data-testid="city-input"
                  required
                />
              </div>

              {/* Product */}
              <div className="space-y-2">
                <Label className="text-gray-900 font-medium">
                  Produit <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.product}
                  onValueChange={(value) => handleSelectChange('product', value)}
                >
                  <SelectTrigger className="border-gray-300 focus:border-burgundy focus:ring-burgundy" data-testid="product-select">
                    <SelectValue placeholder="Choisir un produit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="poulet-frais">🐔 Poulet Frais - 3 500 F CFA</SelectItem>
                    <SelectItem value="poulet-congele">❄️ Poulet Congelé - 3 500 F CFA</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label htmlFor="quantity" className="text-gray-900 font-medium">
                  Quantité <span className="text-red-500">*</span> <span className="text-sm text-gray-500">(minimum 2 poulets)</span>
                </Label>
                <Input
                  id="quantity"
                  name="quantity"
                  type="number"
                  min={MIN_QUANTITY}
                  placeholder="Nombre de poulets"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  className={`border-gray-300 focus:border-burgundy focus:ring-burgundy ${quantityError ? 'border-red-500' : ''}`}
                  data-testid="quantity-input"
                  required
                />
                {quantityError && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle size={14} />
                    {quantityError}
                  </p>
                )}
              </div>

              {/* Delivery Mode */}
              <div className="space-y-2">
                <Label className="text-gray-900 font-medium">
                  Mode de réception <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.deliveryMode}
                  onValueChange={(value) => handleSelectChange('deliveryMode', value)}
                >
                  <SelectTrigger className="border-gray-300 focus:border-burgundy focus:ring-burgundy" data-testid="delivery-mode-select">
                    <SelectValue placeholder="Choisir un mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="livraison">🚚 Livraison</SelectItem>
                    <SelectItem value="retrait">🏪 Retrait sur place</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Delivery Zone - Only show if delivery is selected */}
              {formData.deliveryMode === 'livraison' && (
                <div className="space-y-2">
                  <Label className="text-gray-900 font-medium">
                    Zone de livraison <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={formData.deliveryZone}
                    onValueChange={(value) => handleSelectChange('deliveryZone', value)}
                  >
                    <SelectTrigger className="border-gray-300 focus:border-burgundy focus:ring-burgundy" data-testid="delivery-zone-select">
                      <SelectValue placeholder="Sélectionnez votre zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_ZONES.map((zone) => (
                        <SelectItem key={zone.id} value={zone.id}>
                          {zone.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.deliveryZone === 'above-100km' && (
                    <p className="text-amber-600 text-sm flex items-center gap-1 bg-amber-50 p-2 rounded">
                      <AlertCircle size={14} />
                      Les frais de livraison seront confirmés après validation.
                    </p>
                  )}
                </div>
              )}

              {/* Delivery Date */}
              <div className="space-y-2">
                <Label htmlFor="deliveryDate" className="text-gray-900 font-medium">
                  <Calendar className="inline mr-1" size={16} />
                  Date de livraison souhaitée <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="deliveryDate"
                  name="deliveryDate"
                  type="date"
                  min={today}
                  value={formData.deliveryDate}
                  onChange={handleInputChange}
                  className="border-gray-300 focus:border-burgundy focus:ring-burgundy"
                  data-testid="delivery-date-input"
                  required
                />
                <p className="text-gray-500 text-sm">La date sera confirmée après validation de votre commande.</p>
              </div>

              {/* Confirmation Method */}
              <div className="space-y-2">
                <Label className="text-gray-900 font-medium">
                  Comment souhaitez-vous être contacté ? <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.confirmationMethod}
                  onValueChange={(value) => handleSelectChange('confirmationMethod', value)}
                >
                  <SelectTrigger className="border-gray-300 focus:border-burgundy focus:ring-burgundy" data-testid="confirmation-method-select">
                    <SelectValue placeholder="Choisir une méthode de confirmation" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONFIRMATION_METHODS.map((method) => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.id === 'phone' && '📞 '}
                        {method.id === 'whatsapp' && '💬 '}
                        {method.id === 'sms' && '📱 '}
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label htmlFor="comment" className="text-gray-900 font-medium">
                  Commentaire (optionnel)
                </Label>
                <Textarea
                  id="comment"
                  name="comment"
                  placeholder="Ex: urgence, préférence, commande régulière..."
                  value={formData.comment}
                  onChange={handleInputChange}
                  className="border-gray-300 focus:border-burgundy focus:ring-burgundy min-h-[100px]"
                  data-testid="comment-input"
                />
              </div>

              {/* Price Summary */}
              {formData.product && formData.quantity && parseInt(formData.quantity) >= MIN_QUANTITY && (
                <div className="bg-gold/10 rounded-xl p-4 border border-gold/30">
                  <h3 className="font-semibold text-burgundy mb-3">💰 Récapitulatif</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Sous-total ({formData.quantity} × 3 500 F)</span>
                      <span className="font-medium">{priceCalculation.subtotal.toLocaleString()} F CFA</span>
                    </div>
                    {formData.deliveryMode === 'livraison' && formData.deliveryZone && (
                      <div className="flex justify-between">
                        <span>Frais de livraison</span>
                        <span className="font-medium">
                          {priceCalculation.deliveryFeeText || `${priceCalculation.deliveryFee.toLocaleString()} F CFA`}
                        </span>
                      </div>
                    )}
                    <div className="h-px bg-burgundy/20 my-2"></div>
                    <div className="flex justify-between text-lg font-bold text-burgundy">
                      <span>TOTAL</span>
                      <span>
                        {priceCalculation.isAbove100km 
                          ? `${priceCalculation.subtotal.toLocaleString()} F CFA + livraison`
                          : `${priceCalculation.total.toLocaleString()} F CFA`
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Marketing Consent */}
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <Checkbox
                  id="acceptMarketing"
                  checked={formData.acceptMarketing}
                  onCheckedChange={handleCheckboxChange}
                />
                <label 
                  htmlFor="acceptMarketing" 
                  className="text-sm text-gray-600 cursor-pointer"
                >
                  J'accepte d'être contacté(e) pour recevoir des informations et offres futures de Volaille Prime.
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || isSuccess || !!quantityError}
                className={`w-full text-lg font-semibold transition-all duration-300 ${
                  isSuccess 
                    ? 'bg-green-600 hover:bg-green-600' 
                    : 'bg-burgundy hover:bg-burgundy-dark hover:scale-[1.02]'
                }`}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Envoi en cours...
                  </>
                ) : isSuccess ? (
                  <>
                    <CheckCircle2 className="mr-2" size={20} />
                    Commande envoyée !
                  </>
                ) : (
                  'Envoyer la commande'
                )}
              </Button>
            </form>

            {/* Important Info Cards */}
            <div className="mt-8 space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-1">ℹ️ Information importante</h4>
                <p className="text-sm text-blue-700">
                  Les commandes sont traitées selon la disponibilité liée aux cycles de production. Les précommandes permettent une meilleure planification.
                </p>
              </div>
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-1">💳 Paiement</h4>
                <p className="text-sm text-gray-600">
                  Paiement au nom de <strong>DMB Consulting</strong>. Le paiement sera confirmé lors de la validation de votre commande.
                </p>
              </div>
            </div>
            </>
            )}
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
